import axiosInstance from "../config/api";
import { makeAutoObservable, runInAction } from "mobx";
import env from "../config/env";
import i18n from "../locales/i18n";

export type User = {
  country?: string;
  email?: string;
  fullName?: string;
  gender?: string;
  goal?: string;
  governorate?: string;
  password?: string;
  phone?: string;
  state?: string;
  plan?: string;
  situation?: string;
  interest?: string[];
  id?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  linkedInUrl?: string;
  githubUrl?: string;
  birthday?: Date;
};

export class UserStore {
  user: any = null;
  userData: User | null = null;
  currency: string = "USD";
  language: string = "English";
  paymentMethod:
    | "PostWire"
    | "Onsite"
    | "OnlinePayment"
    | "KonnectPayment"
    | "StripePayment" = "Onsite";

  constructor() {
    makeAutoObservable(this);
    this.currency = localStorage.getItem("currency") || "USD";
    this.language = localStorage.getItem("language") || "English";
  }

  setInitialState() {
    this.user = null;
    this.userData = null;
    this.currency = "USD";
    this.language = "English";
  }

  async getUser() {
    const token = localStorage.getItem("token");
    // const user = localStorage.getItem("user");

    if (token) {
      const response = await axiosInstance.get(
        `/Profiles/CurrentUser?access_token=${token}`
      );
      if (response.status === 200) {
        runInAction(() => {
          this.user = response.data.user;
          this.currency = response.data.user.currency || "USD";
          this.language = response.data.user.language || "English";

          localStorage.setItem("currency", this.currency);
          localStorage.setItem("language", this.language);
          // localStorage.setItem("user", JSON.stringify(this.user));
        });
      }
    }
  }

  setCurrency(newCurrency: string) {
    runInAction(() => {
      this.currency = newCurrency;
      localStorage.setItem("currency", newCurrency);
    });
    if (this.user?.id) {
      this.updateProfile(this.user.id, { currency: newCurrency });
    }
  }

  setLanguage(newLanguage: any) {
    runInAction(() => {
      this.language = newLanguage.label;
      localStorage.setItem("language", this.language);
      i18n.changeLanguage(newLanguage.value);
    });
    if (this.user?.id) {
      this.updateProfile(this.user.id, { language: newLanguage.label });
    }
  }

  setUserData(user: User) {
    runInAction(() => {
      this.userData = user;
    });
  }

  async login(email: string, password: string): Promise<any | null> {
    const data = {
      email,
      password,
    };
    const response = await axiosInstance.post(
      `/Profiles/userSpecialLogin`,
      data
    );
    if (response.status === 200) {
      runInAction(() => {
        this.user = response.data.userData;
        localStorage.setItem("token", response.data.id);
        localStorage.removeItem("tempToken");
        localStorage.removeItem("registrationStep");
        localStorage.removeItem("registrationUser");
      });
      this.getUser();
      if (!response.data?.completedRegistrationSteps) {
        localStorage.setItem(
          "registrationUser",
          JSON.stringify(response.data?.userData)
        );
        if (env.REACT_APP_APP_ENV === "development") {
          localStorage.setItem(
            "registrationStep",
            response.data?.userData?.registrationStep
          );
          setTimeout(() => {
            localStorage.setItem(
              "registrationStep",
              response.data?.userData?.registrationStep + 1
            );
            window.location.replace("/");
          }, 2000);
        } else {
          localStorage.setItem(
            "registrationStep",
            response.data?.userData?.registrationStep + 1
          );
        }
      }
      return response.data;
    } else {
      return null;
    }
  }

  async loginWithGoogle(token: string): Promise<string | null> {
    const response = await axiosInstance.post(`/Profiles/googleLogin`, {
      token: token,
    });
    if (response.status === 200) {
      runInAction(() => {
        this.user = response.data.userData;
        localStorage.setItem("token", response.data.id);
      });
      this.getUser();
      return response.data.id;
    } else {
      return null;
    }
  }

  async register(data: {
    // birthday: string,
    country: string;
    email: string;
    fullName: string;
    gender: string;
    goal: string;
    governorate: string;
    password: string;
    phone: string;
    state: string;
    interest: string[];
    step: number;
  }): Promise<string | null> {
    const response = await axiosInstance.post("/Profiles", {
      birthday: new Date().toISOString(),
      country: data.country,
      email: data.email,
      fullName: data.fullName,
      gender: data.gender,
      goal: data.goal,
      governorate: data.governorate,
      password: data.password,
      phone: data.phone,
      state: data.state,
      interest: data.interest,
      step: data.step,
    });
    if (response.status === 200) {
      const userId = response.data.id;
      const setStepResponse = await axiosInstance.patch(
        `/Profiles/setStep?id=${userId}&step=${data.step}`
      );
      if (setStepResponse.status === 200) {
        return setStepResponse.data.id;
      } else {
        throw new Error("Failed to update the step.");
      }
    } else {
      throw new Error("Unexpected response status: " + response.status);
    }
  }

  async signUpWithGoogle(token: string): Promise<string | null> {
    const response = await axiosInstance.post(`/Profiles/signUpGoogle`, {
      token: token,
    });
    if (response.status === 200) {
      runInAction(() => {
        this.user = response.data.profile;
        localStorage.setItem("token", response.data.id);
      });
      this.getUser();
      return response.data.id;
    } else {
      return null;
    }
  }

  async signUpWithLinkedIn(code: string): Promise<string | null> {
    const response = await axiosInstance.post(`/Profiles/signUpLinkedIn`, {
      code: code,
    });
    if (response.status === 200) {
      runInAction(() => {
        this.user = response.data.profile;
        localStorage.setItem("token", response.data.id);
      });
      this.getUser();
      return response.data.id;
    } else {
      return null;
    }
  }

  async loginWithLinkedIn(code: string): Promise<string | null> {
    const response = await axiosInstance.post(`/Profiles/linkedinLogin`, {
      code: code,
    });
    if (response.status === 200) {
      runInAction(() => {
        console.log("response.data", response.data);

        this.user = response.data.profile;
        localStorage.setItem("token", response.data.id);
      });
      this.getUser();
      return response.data.id;
    } else {
      return null;
    }
  }

  async verify(token: string, uid: string): Promise<string | null> {
    const response = await axiosInstance.get(
      `/Profiles/confirm?uid=${uid}&token=${token}`
    );
    if (response.status === 200) {
      runInAction(() => {
        localStorage.setItem("token", response.data.token);
      });
      this.getUser();
      return response.data.token;
    } else {
      return null;
    }
  }

  // async forgotPassword(email: string): Promise<string | null> {
  //   const response = await axiosInstance.post("/Profiles/reset", {
  //     email: email,
  //   });
  //   if (response.status === 200 || response.status === 204) {
  //     return "A password reset link has been successfully sent to your email address. Please check your inbox.";
  //   } else {
  //     return null;
  //   }
  // }

  async forgotPassword(email: string): Promise<string | null> {
    const response = await axiosInstance.post(
      "/Profiles/generateForgotPasswordCode",
      {
        email: email,
      }
    );
    return response.data;
  }

  async resendForgotPasswordCode(
    email: string
  ): Promise<{ status: string; message: string }> {
    const response = await axiosInstance.post(
      "/Profiles/resendForgotPasswordCode",
      {
        email: email,
      }
    );
    return response.data;
  }

  async verifyForgotPasswordCode(
    email: string,
    code: any
  ): Promise<{ status: string; message: string }> {
    const response = await axiosInstance.post(
      "/Profiles/verifyForgotPasswordCode",
      {
        email: email,
        code: code,
      }
    );
    return response.data;
  }

  async resetPassword(
    accessToken: string,
    newPassword: string
  ): Promise<string | null> {
    const response = await axiosInstance.post(
      `/Profiles/reset-password?access_token=${accessToken}`,
      { newPassword }
    );
    if (response.status !== 200 && response.status !== 204) {
      if (response.status === 401) {
        throw new Error("Reset password link has expired.");
      } else {
        throw new Error("Error resetting password.");
      }
    } else {
      return "Your password has successfully changes.";
    }
  }

  async forgotPasswordWithCode(
    email: string,
    code: string,
    newPassword: string
  ): Promise<string | null> {
    try {
      const response = await axiosInstance.post(
        "/Profiles/forgotPasswordWithCode",
        {
          email,
          code,
          newPassword,
        }
      );
      if (response.status === 200) {
        return "User password updated successfully";
      } else {
        return null;
      }
    } catch (error) {
      throw new Error("Error resetting password");
    }
  }

  logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("registrationStep");
    localStorage.removeItem("registrationUser");
    localStorage.removeItem("user");
    this.user = null;
  }

  paymentMethodHandler(
    method:
      | "PostWire"
      | "Onsite"
      | "OnlinePayment"
      | "KonnectPayment"
      | "StripePayment"
  ) {
    runInAction(() => {
      this.paymentMethod = method;
    });
  }

  async sendUpgradeRequest(
    upgradeType: string,
    profileId: string,
    classroom?: any
  ) {
    const data: any = {
      newRole: upgradeType,
      //data: {},
      status: "Pending",
      profileId: profileId,
    };
    if (upgradeType === "teacher") {
      data.data = {};
    } else {
      data.profile = {};
      data.room = {
        name: classroom?.name || "",
        description: classroom?.description || "",
        size: classroom?.capacity || 6,
        to: new Date(),
        from: new Date(),
        location: classroom?.location || {
          lat: 0,
          lng: 0,
        },
        address: classroom?.address || {
          name: "",
          area: "",
          city: "",
          state: "",
        },
        album: classroom?.files || [],
        logo: classroom?.files[0],
        equipments: classroom?.equipments || [],
        profileId: profileId,
      };
      if (classroom?.charge) {
        data.room.pricePerHour = classroom?.charge;
      }
      if (classroom?.days) {
        data.room.days = classroom?.days.filter((day: any) => day.enabled);
      }
      console.log(classroom, data.room);
    }
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await axiosInstance.post(
          `/Profiles/${profileId}/upgradeRequest?access_token=${token}`,
          data
        );

        return response.status === 200
          ? {
              CODE: "SUCCESS",
              STATUS: response.status,
              MESSAGE:
                "Your upgrade to Instructor request has been successfully sent",
            }
          : {
              CODE: "ERROR",
              STATUS: response.status,
              MESSAGE: "Something went wrong. Please try again later",
            };
      } catch (error) {
        throw error;
      }
    } else {
      return {
        CODE: "ERROR",
        STATUS: 401,
        MESSAGE: "Please login to your account first.",
      };
    }
  }

  async updateProfile(id: string, data: any): Promise<string | null> {
    const token = localStorage.getItem("token");

    const response = await axiosInstance.post(
      `/Profiles/updateProfile/${id}?access_token=${token}`,
      data
    );
    if (response.status === 200) {
      runInAction(() => {
        this.getUser();
      });
      return response.data;
    } else {
      return null;
    }
  }

  async confirmPassword(password: string): Promise<any> {
    const token = localStorage.getItem("token");
    const response = await axiosInstance.get(
      `/Profiles/confirmPassword?currentPassword=${password}&access_token=${token}`
    );
    if (response.status === 200) {
      runInAction(() => {
        this.user = response.data.userData;
      });
      this.getUser();
      return response.data;
    } else {
      return null;
    }
  }

  async updatePassword(password: string): Promise<any | null> {
    const token = localStorage.getItem("token");
    const response = await axiosInstance.post(
      `/Profiles/updatePassword?access_token=${token}`,
      { newPassword: password }
    );
    if (response.status === 200) {
      runInAction(() => {
        localStorage.setItem("token", response.data.id);
      });
      this.getUser();
      return response.data;
    } else {
      return null;
    }
  }

  async uploadPicture(picture: File): Promise<any> {
    const imageData = new FormData();
    imageData.append("file", picture);

    const response = await axiosInstance.post(
      "/Containers/img/upload",
      imageData
    );

    if (response.status === 200) {
      return response.data.result.files.file[0];
    } else {
      return null;
    }
  }

  async uploadFile(file: File): Promise<any> {
    const fileData = new FormData();
    fileData.append("file", file);

    const response = await axiosInstance.post(
      "/Container2s/file/upload",
      fileData
    );

    if (response.status === 200) {
      return response.data.result.files.file[0];
    } else {
      return null;
    }
  }

  async checkEmail(email: string): Promise<any> {
    try {
      const response = await axiosInstance.post(`/Profiles/checkEmail`, {
        email,
      });
      if (response.status === 200) {
        return response.data.data.value;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  async sendVerificationCode(email: string): Promise<any> {
    try {
      const response = await axiosInstance.post(
        `/Profiles/registerWithVerificationCode`,
        { email }
      );
      if (response.status === 200) {
        runInAction(() => {
          localStorage.setItem("token", response.data.token.id);
        });
        this.getUser();
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  async verifyCode(verificationCode: string): Promise<any> {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("tempToken");
      const response = await axiosInstance.post(
        `/Profiles/verifyEmailWithCode?access_token=${token}`,
        { verificationCode }
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  async resendVerificationCode(): Promise<any> {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("tempToken");
      const response = await axiosInstance.post(
        `/Profiles/resendVerificationCode?access_token=${token}`
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  async sendMetaEvent(eventName: string, eventData: any, metadata: any = {}) {
    console.log("env.REACT_APP_APP_ENV", env.REACT_APP_APP_ENV);

    if (env.REACT_APP_APP_ENV === "production") {
      try {
        const response = await axiosInstance.post(
          `https://graph.facebook.com/v14.0/${env.REACT_APP_PIXEL_ID}/events`,
          { name: eventName, data: [eventData], metadata },
          {
            headers: {
              Authorization: `Bearer ${env.REACT_APP_META_ACCESS_TOKEN}`,
            },
          }
        );

        if (response.status === 200 || response.status === 201) {
          console.log("Event sent successfully:", eventName);
        } else {
          console.warn("Failed to send event:", response.status, response.data);
        }
      } catch (error) {
        console.error("Error sending event:", eventName, error);
      }
    }
  }

  async createTempUser(user: User): Promise<any> {
    try {
      const response = await axiosInstance.post(
        `/Profiles/createTempUser`,
        user
      );
      if (response.status === 200) {
        runInAction(() => {
          localStorage.setItem("tempToken", response.data.token.id);
        });
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  async getMyCoursesFeedbacks(): Promise<any> {
    try {
      let token = localStorage.getItem("token");
      if (!token) return;

      if (!this.user) {
        await this.getUser();
      }
      const response = await axiosInstance.get(
        `/Profiles/${this.user.id}/given-course-feedbacks`
      );
      if (response.status === 200) {
        return response.data.feedbacks;
      }
      return [];
    } catch (error) {
      console.error("Error getting my courses feedbacks: ", error);
    }
  }

  async deleteMyCourseFeedback(courseFeedbackId: string): Promise<any> {
    try {
      let token = localStorage.getItem("token");
      if (!token) return;

      const response = await axiosInstance.delete(
        `/Profiles/${courseFeedbackId}/delete-course-feedback`
      );

      if (response.status === 200) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Error deleting my course feedback", error);
    }
  }

  async getMyInstructorsFeedbacks(): Promise<any> {
    try {
      let token = localStorage.getItem("token");
      if (!token) return;

      if (!this.user) {
        await this.getUser();
      }
      const response = await axiosInstance.get(
        `/Profiles/${this.user.id}/given-teacher-feedbacks`
      );
      if (response.status === 200) {
        return response.data.feedbacks;
      }
      return [];
    } catch (error) {
      console.error("Error getting my instructors feedbacks: ", error);
    }
  }

  async deleteMyInstructorFeedback(instructorFeedbackId: string): Promise<any> {
    try {
      let token = localStorage.getItem("token");
      if (!token) return;

      const response = await axiosInstance.delete(
        `/Profiles/${instructorFeedbackId}/delete-teacher-feedback`
      );

      if (response.status === 200) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Error deleting my instructor feedback", error);
    }
  }

  async getMyClassroomsFeedbacks(): Promise<any> {
    try {
      let token = localStorage.getItem("token");
      if (!token) return;

      if (!this.user) {
        await this.getUser();
      }
      const response = await axiosInstance.get(
        `/Profiles/${this.user.id}/given-classroom-feedbacks`
      );
      if (response.status === 200) {
        return response.data.feedbacks;
      }
      return [];
    } catch (error) {
      console.error("Error getting my classrooms feedbacks: ", error);
    }
  }

  async deleteMyClassroomFeedback(classroomFeedbackId: string): Promise<any> {
    try {
      let token = localStorage.getItem("token");
      if (!token) return;

      const response = await axiosInstance.delete(
        `/Profiles/${classroomFeedbackId}/delete-classroom-feedback`
      );

      if (response.status === 200) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Error deleting my classroom feedback", error);
    }
  }

  async addReview(reviewData: any): Promise<any> {
    let token = localStorage.getItem("token");
    if (!token) return;
    try {
      const response = await axiosInstance.post(
        `/Profiles/giveNote?access_token=${token}`,
        reviewData
      );
      if (response.status === 204) {
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error adding review", error);
      return false;
    }
  }
}
